import findKey from 'lodash/findKey';
import useClientRoutesContext from './useClientRoutesContext';
import { RouteName } from '../types';
import { useCallback, useEffect, useMemo } from 'react';
import { UrlParams } from '../../../modules/shared/types';
import { useCurrentLanguage } from 'src/config/i18n';
import { useLocation, useParams } from 'react-router-dom';
import { useAppDispatch } from 'src/modules/shared/hooks/useAppDispatch';
import { setCurrentRouteName } from 'src/modules/shared/slices/privatePageSettings/entity/privatePageSettingsSlice';

const useI18nPath = () => {
  const routes = useClientRoutesContext();
  const currentLanguage = useCurrentLanguage();
  const location = useLocation();
  const params = useParams<UrlParams>();
  const dispatch = useAppDispatch();

  const getPath = useCallback(
    (routeName: RouteName, replacements?: { [key: string]: string | undefined }): string => {
      if (!routes[routeName]) {
        console.warn(`You are trying to navigate to an unknown route '${routeName}'`);
        return '';
      }

      if (!replacements) return routes[routeName];

      return routes[routeName].split('/').reduce((acc, curr) => {
        const match = curr.match(/^:(.*)/);
        if (!match) return `${acc}/${curr}`;

        const replacement = replacements[match[1]] || '';
        return `${acc}/${replacement}`;
      });
    },
    [routes]
  );

  useEffect(() => {
    const match = findKey(routes, (route: string) => {
      const regExp = new RegExp(`/${currentLanguage}/`, 'i');

      route = route.replace(regExp, '');

      // replace params in path by their placeholder equivalent
      let processedPath: string = location.pathname
        .replace(params.bookId || 'NOT_IN_PATH', ':bookId')
        .replace(params.chapterId || 'NOT_IN_PATH', ':chapterId')
        .replace(params.trajectoryId || 'NOT_IN_PATH', ':trajectoryId')
        .replace(params.sectionCode || 'NOT_IN_PATH', ':sectionCode?')
        .replace(params.sectionName || 'NOT_IN_PATH', ':sectionName?')
        .replace(params.seriesId || 'NOT_IN_PATH', ':seriesId?')
        .replace(regExp, '')
        .replace(/\/$/, '');

      // if there is a trajectoryId, the route always includes the optional params placeholders
      if (params.trajectoryId != null) {
        if (!processedPath.includes(':sectionCode?')) processedPath += ':sectionCode?/';
        if (!processedPath.includes(':sectionName?')) processedPath += ':sectionName?/';
        if (!processedPath.includes(':seriesId?')) processedPath += ':seriesId?';
      }

      return route === processedPath;
    });

    dispatch(setCurrentRouteName(match))
  }, [currentLanguage, dispatch, location.pathname, params, routes])

  const currentRouteName = useMemo(() => {
    const match = findKey(routes, (route: string) => {
      const regExp = new RegExp(`/${currentLanguage}/`, 'i');

      route = route.replace(regExp, '');

      // replace params in path by their placeholder equivalent
      let processedPath: string = location.pathname
        .replace(params.bookId || 'NOT_IN_PATH', ':bookId')
        .replace(params.chapterId || 'NOT_IN_PATH', ':chapterId')
        .replace(params.trajectoryId || 'NOT_IN_PATH', ':trajectoryId')
        .replace(params.sectionCode || 'NOT_IN_PATH', ':sectionCode?')
        .replace(params.sectionName || 'NOT_IN_PATH', ':sectionName?')
        .replace(params.seriesId || 'NOT_IN_PATH', ':seriesId?')
        .replace(regExp, '')
        .replace(/\/$/, '');

      // if there is a trajectoryId, the route always includes the optional params placeholders
      if (params.trajectoryId != null) {
        if (!processedPath.includes(':sectionCode?')) processedPath += ':sectionCode?/';
        if (!processedPath.includes(':sectionName?')) processedPath += ':sectionName?/';
        if (!processedPath.includes(':seriesId?')) processedPath += ':seriesId?';
      }

      return route === processedPath;
    });

    return match;
  }, [routes, currentLanguage, location.pathname, params]);

  const getRouteNameFromPath = useCallback(
    (path: string): RouteName | null =>
      (findKey(routes, (route: string) => {
        // replace params in path by their placeholder equivalent
        let processedPath: string = path
          .replace(params.bookId || 'NOT_IN_PATH', ':bookId')
          .replace(params.chapterId || 'NOT_IN_PATH', ':chapterId')
          .replace(params.trajectoryId || 'NOT_IN_PATH', ':trajectoryId')
          .replace(params.sectionCode || 'NOT_IN_PATH', ':sectionCode?')
          .replace(params.sectionName || 'NOT_IN_PATH', ':sectionName?')
          .replace(params.seriesId || 'NOT_IN_PATH', ':seriesId?');

        // if there is a trajectoryId, the route always includes the optional params placeholders
        if (params.trajectoryId != null) {
          if (!processedPath.includes(':sectionCode?')) processedPath += ':sectionCode?/';
          if (!processedPath.includes(':sectionName?')) processedPath += ':sectionName?/';
          if (!processedPath.includes(':seriesId?')) processedPath += ':seriesId?';
        }

        return route === processedPath;
      }) as RouteName) || null,
    [routes, params]
  );

  return { getPath, getRouteNameFromPath, currentRouteName };
};

export default useI18nPath;
